/**
 * El filtro de fechas de la lista de Ventas, como Aliclik: QUÉ fecha (registro, creación, despacho,
 * entrega, actualización) y ENTRE cuándo (founder, 25-sep).
 *
 * ⭐ SE FILTRA EN LA BASE, NO EN LA PANTALLA: la lista pagina en el servidor, y filtrar lo ya traído
 *    mostraría la primera página filtrada y mentiría sobre el resto.
 *
 * 🪤 DESPACHO Y ENTREGA SON FECHAS DEL ENVÍO (`despachado_en`, `entregado_en`, F-A), no del pedido.
 *    Filtrar por un campo de un embebido exige `!inner`: sin eso PostgREST trae el pedido igual, con
 *    la lista de envíos vacía, y el filtro no filtra nada. Se pide con un alias propio para no tocar
 *    el embebido de siempre (`shipments`), que sí tiene que traer todos los envíos.
 */
export type TipoDeFecha = 'registro' | 'creacion' | 'despacho' | 'entrega' | 'actualizacion';

export const TIPOS_DE_FECHA: { value: TipoDeFecha; label: string }[] = [
  { value: 'registro', label: 'Registro' },
  { value: 'creacion', label: 'Creación' },
  { value: 'despacho', label: 'Despacho' },
  { value: 'entrega', label: 'Entrega' },
  { value: 'actualizacion', label: 'Actualización' },
];

const COLUMNA: Record<TipoDeFecha, string> = {
  registro: 'fecha_efectiva',
  creacion: 'created_at',
  despacho: 'envio_por_fecha.despachado_en',
  entrega: 'envio_por_fecha.entregado_en',
  actualizacion: 'updated_at',
};

export interface FiltroDeFecha {
  fechaTipo?: TipoDeFecha;
  /** `YYYY-MM-DD`, inclusive. */
  fechaDesde?: string;
  /** `YYYY-MM-DD`, inclusive (se filtra hasta el día siguiente, exclusivo). */
  fechaHasta?: string;
}

interface Filtrable<Q> {
  gte(columna: string, valor: string): Q;
  lt(columna: string, valor: string): Q;
}

const diaSiguiente = (dia: string) => {
  const d = new Date(`${dia}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** Lo que el filtro le suma a la consulta: el embebido que necesite y los topes de fecha. */
export function filtroDeFecha(f: FiltroDeFecha) {
  const activo = !!(f.fechaDesde || f.fechaHasta);
  const tipo = f.fechaTipo ?? 'registro';
  const delEnvio = activo && (tipo === 'despacho' || tipo === 'entrega');
  return {
    embebido: delEnvio ? ', envio_por_fecha:order_shipments!inner(despachado_en, entregado_en)' : '',
    aplicar<Q extends Filtrable<Q>>(query: Q): Q {
      if (!activo) return query;
      let q = query;
      // Los días son de Lima: el día 1 empieza a las 00:00 de Perú, no de Greenwich.
      if (f.fechaDesde) q = q.gte(COLUMNA[tipo], `${f.fechaDesde}T00:00:00-05:00`);
      if (f.fechaHasta) q = q.lt(COLUMNA[tipo], `${diaSiguiente(f.fechaHasta)}T00:00:00-05:00`);
      return q;
    },
  };
}
